import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from '@platform/core/lib/cx';
import type { ISegmentedProps } from './ISegmentedProps';
import { rovingKey } from './rovingKey';
import { segmentedSplit } from './segmentedSplit';
import './segmented.css';

/**
 * Сегментований перемикач (UIUX/controls/segmented-control): 2–8 взаємовиключних варіантів у сірому контейнері з відступом 2px;
 * вибраний сегмент — біла плашка з тінню; висота sm 26 · md 28 · lg 30px. Підписи не переносяться (обрізаються «…», повний текст — у
 * підказці). Понад 8 варіантів — перші 7 сегментами, решта у списку «Ще…» (вибраний із нього — підсвічений список). Клавіатура
 * (radiogroup): у порядку Tab — лише вибраний сегмент, стрілки й Home/End вибирають сусідній сегмент.
 */
export function Segmented<T extends string>({ options, value, onChange, size = 'md', label }: ISegmentedProps<T>) {
  const { t } = useTranslation();
  const { shown, more } = segmentedSplit(options);
  const inMore = more.some((o) => o.value === value);
  const move = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const next = rovingKey(e.key, index, shown.length);
    if (next === null) return;
    e.preventDefault();
    onChange(shown[next]!.value);
    (e.currentTarget.parentElement?.children[next] as HTMLElement | undefined)?.focus();
  };
  return (
    <div className={cx('segmented', size !== 'md' && `segmented--${size}`)} role="radiogroup" aria-label={label}>
      {shown.map((o, i) => (
        <button key={o.value} type="button" role="radio" aria-checked={o.value === value} title={o.label}
          tabIndex={o.value === value || (!shown.some((x) => x.value === value) && i === 0) ? 0 : -1}
          className={cx('segmented__btn', o.value === value && 'is-active')} onClick={() => onChange(o.value)} onKeyDown={(e) => move(e, i)}>
          {o.label}
        </button>
      ))}
      {more.length > 0 && (
        <select className={cx('segmented__more', inMore && 'is-active')} value={inMore ? value : ''} aria-label={t('viewsKit.moreOptions')}
          onChange={(e) => { if (e.target.value) onChange(e.target.value as T); }}>
          {!inMore && <option value="">{t('viewsKit.more')}</option>}
          {more.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      )}
    </div>
  );
}
